import { uniqueProperties } from '../components/Publications/publicationModel';
// src/hooks/useProperties.js
import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchProperties as apiFetchProperties } from '../api/propertiesApi';

const resultCache = new Map();
const CACHE_TTL = 30_000;
const cachedResult = key => { const entry = resultCache.get(key); return entry && Date.now() - entry.at < CACHE_TTL ? entry.value : null; };
const inflight = new Map();

const searchKey = (filters, polygons) => JSON.stringify([filters, polygons]);

const toApiFilters = (effective, requestPolygons) => {
  const apiFilters = {};
  if (effective.searchValue) apiFilters.search = effective.searchValue;
  if (effective.transactionType) apiFilters.type_operation = effective.transactionType || null;
  if (effective.typeProperty) apiFilters.type_property = effective.typeProperty;
  if (effective.minPrice !== undefined && effective.minPrice !== null && effective.minPrice !== '')
    apiFilters.min_price = Number(effective.minPrice);
  if (effective.maxPrice !== undefined && effective.maxPrice !== null && effective.maxPrice !== '')
    apiFilters.max_price = Number(effective.maxPrice);
  if (effective.currency) apiFilters.price_currency = effective.currency;
  if (effective.minBedrooms) apiFilters.min_bedrooms = effective.minBedrooms || null;
  if (effective.maxBedrooms) apiFilters.max_bedrooms = effective.maxBedrooms || null;
  if (effective.minBathrooms) apiFilters.min_bathrooms = effective.minBathrooms || null;
  if (effective.maxBathrooms) apiFilters.max_bathrooms = effective.maxBathrooms || null;
  if (effective.minSquareMetersCovered != null && effective.minSquareMetersCovered !== '')
    apiFilters.min_square_meters_covered = Number(effective.minSquareMetersCovered);
  if (effective.maxSquareMetersCovered != null && effective.maxSquareMetersCovered !== '')
    apiFilters.max_square_meters_covered = Number(effective.maxSquareMetersCovered);
  if (effective.minSquareMetersTotal != null && effective.minSquareMetersTotal !== '')
    apiFilters.min_square_meters_total = Number(effective.minSquareMetersTotal);
  if (effective.maxSquareMetersTotal != null && effective.maxSquareMetersTotal !== '')
    apiFilters.max_square_meters_total = Number(effective.maxSquareMetersTotal);
  if (effective.minParking !== undefined && effective.minParking !== null && effective.minParking !== '')
    apiFilters.min_parking = Math.max(0, Number(effective.minParking));
  if (effective.hasPool) apiFilters.has_pool = 1;
  if (effective.hasGrill) apiFilters.has_grill = 1;
  if (effective.hasGarden) apiFilters.has_garden = 1;
  if (effective.hasTerrace) apiFilters.has_terrace = 1;
  if (effective.hasBalcony) apiFilters.has_balcony = 1;
  if (effective.minRooms) apiFilters.min_rooms = effective.minRooms || null;
  if (effective.maxRooms) apiFilters.max_rooms = effective.maxRooms || null;
  if (effective.minAntiquity) apiFilters.min_antiquity = effective.minAntiquity || null;
  if (effective.maxAntiquity) apiFilters.max_antiquity = effective.maxAntiquity || null;
  if (effective.ownerType) apiFilters.owner_type = effective.ownerType || null;

  const hasPolygons = Array.isArray(requestPolygons) && requestPolygons.length > 0;
  if (hasPolygons) {
    apiFilters.polygon = JSON.stringify(requestPolygons);
  } else if (effective.radius && effective.center) {
    apiFilters.lat = effective.center.lat;
    apiFilters.lng = effective.center.lng;
    apiFilters.radius = effective.radius;
  }
  return apiFilters;
};

export const transformProperty = (prop) => ({
  ...prop,
  id: prop.id,
  url: prop.url,
  name: prop.description || `Propiedad en ${prop.location}`,
  price: prop.price_value,
  price_currency: prop.price_currency,
  rooms: prop.rooms,
  bedrooms: prop.bedrooms,
  bathrooms: prop.bathrooms,
  type: prop.type,
  location: prop.location,
  address: prop.address,
  lat: prop.latitude,
  lng: prop.longitude,
  square_meters_covered: prop.square_meters_covered,
  square_meters_total: prop.square_meters_total,
  expenses_value: prop.expenses_value,
  expenses_currency: prop.expenses_currency,
  antiquity: prop.antiquity,
  is_active: prop.is_active,
  image_url: prop.image_url,
  hasGarden: prop.has_garden,
  hasPool: prop.has_pool,
  hasGrill: prop.has_grill,
  hasTerrace: prop.has_terrace,
  hasBalcony: prop.has_balcony,
  parking: prop.parking,
  typeOperation: prop.type_operation,
  typeProperty: prop.type_property,
  views: prop.views,
  days_published: prop.days_published,
  owner_type: prop.owner_type,
});

const transformProperties = (data) => data.map(transformProperty);

export function resetPropertySearchCache() {
  resultCache.clear();
  inflight.clear();
}

export function prefetchProperties(filters, polygons = []) {
  const apiFilters = toApiFilters(filters, polygons);
  if (Object.keys(apiFilters).length === 0) return Promise.resolve([]);
  const key = searchKey(filters, polygons);
  if (cachedResult(key)) return Promise.resolve(cachedResult(key));
  if (inflight.has(key)) return inflight.get(key);
  const job = apiFetchProperties(apiFilters)
    .then((data) => {
      const transformed = uniqueProperties(transformProperties(data));
      resultCache.set(key, { value: transformed, at: Date.now() });
      if (resultCache.size > 6) resultCache.delete(resultCache.keys().next().value);
      return transformed;
    })
    .finally(() => inflight.delete(key));
  inflight.set(key, job);
  return job;
}

/**
 * @param filters - filtros del listado
 * @param polygonsPaths - Array<Array<{lat,lng}>> polígonos dibujados por el usuario.
 *                       Si está vacío, el backend ignora el filtro de polígono.
 */
export const useProperties = (
  filters = {},
  polygonsPaths = [],
  { enabled = true, refetchTrigger = 0 } = {}
) => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const propertiesRef = useRef([]);
  const settledRef = useRef(false);
  // The request key represents the exact filters shown by the editor.
  const filtersKey = JSON.stringify([filters, polygonsPaths]);
  const latestRequest = useRef(0);

  const getProperties = useCallback(async () => {
    const [requestFilters, requestPolygons] = JSON.parse(filtersKey);
    const requestId = ++latestRequest.current;
    if (!enabled) {
      setLoading(false);
      return;
    }

    // Si no hay filtros todavía, no pegues a la API y limpia estado.
    if (!requestFilters || Object.keys(requestFilters).length === 0) {
      propertiesRef.current = [];
      setProperties([]);
      setError(null);
      setLoading(false);
      setRefreshing(false);
      return;
    }

    const remember = (list) => {
      propertiesRef.current = list;
      setProperties(list);
    };
    const cached = cachedResult(filtersKey);
    if (cached) {
      remember(cached);
      settledRef.current = true;
      setError(null);
      setLoading(false);
      setRefreshing(false);
      return;
    }

    const updating = settledRef.current;
    setLoading(!updating);
    setRefreshing(updating);

    try {
      const transformed = await prefetchProperties(requestFilters, requestPolygons);
      if (requestId !== latestRequest.current) return;
      remember(transformed);
      settledRef.current = true;
      setError(null);
    } catch (err) {
      if (requestId !== latestRequest.current) return;
      console.error('Error fetching properties:', err);
      setError(err.message);
    } finally {
      if (requestId === latestRequest.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [enabled, filtersKey]);

  useEffect(() => {
    getProperties();
    return () => { latestRequest.current += 1; };
  }, [getProperties, refetchTrigger]);

  return { properties, loading, refreshing, error, refetch: getProperties };
};
